import React, { useState, useMemo, useCallback } from 'react';
import { downloadCsv, exportTableToPdf } from './utils/reportExport';
import { useAuth } from './AuthContext';
import { normalizeRole } from './utils/userLocale';
import './SalesHistory.css';

const SALES_CSV_HEAD_OWNER = [
  'sale_id',
  'date',
  'product',
  'category',
  'quantity',
  'payment_method',
  'payment_label',
  'revenue_tzs',
  'cost_tzs',
  'profit_tzs',
];

const SALES_CSV_HEAD_STAFF = [
  'sale_id',
  'date',
  'product',
  'category',
  'quantity',
  'payment_method',
  'payment_label',
  'revenue_tzs',
];

const SALES_PDF_HEAD_OWNER = [
  'Sale ID',
  'Date',
  'Product',
  'Category',
  'Qty',
  'Payment',
  'Payment detail',
  'Revenue (TZS)',
  'Cost (TZS)',
  'Profit (TZS)',
];

const SALES_PDF_HEAD_STAFF = [
  'Sale ID',
  'Date',
  'Product',
  'Category',
  'Qty',
  'Payment',
  'Payment detail',
  'Revenue (TZS)',
];

function buildSalesExportRows(sales, includeFinancial = true) {
  return sales.map((s) => {
    const base = [
      s.id != null ? String(s.id) : '',
      s.date != null ? String(s.date) : '',
      s.product?.name || '',
      s.product?.category || '',
      s.quantity != null ? String(s.quantity) : '',
      s.paymentMethod || '',
      s.paymentLabel || '',
      s.amount != null ? String(s.amount) : '',
    ];
    if (!includeFinancial) return base;
    return [...base, s.cost != null ? String(s.cost) : '', s.profit != null ? String(s.profit) : ''];
  });
}

function formatSaleDate(iso) {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return String(iso);
    return d.toLocaleString(undefined, {
      dateStyle: 'medium',
      timeStyle: 'short',
    });
  } catch {
    return String(iso);
  }
}

const SalesHistory = ({ sales = [], loading = false, onRefresh }) => {
  const { user } = useAuth();
  const isOwner = normalizeRole(user?.role) === 'owner';
  const [search, setSearch] = useState('');
  const [filterMethod, setFilterMethod] = useState('all');
  const [sortBy, setSortBy] = useState('date');
  const [sortDir, setSortDir] = useState('desc');
  const [pdfExporting, setPdfExporting] = useState(false);
  const [exportError, setExportError] = useState('');

  const paymentMethods = [
    { value: 'all', label: 'All methods' },
    { value: 'cash', label: 'Cash' },
    { value: 'lipa_number', label: 'Lipa number' },
    { value: 'legacy', label: 'Other (legacy)' },
  ];

  const list = useMemo(() => (Array.isArray(sales) ? sales : []), [sales]);

  const filteredSales = useMemo(() => {
    let filtered = list;
    if (filterMethod !== 'all') {
      filtered = filtered.filter((s) => {
        const m = s.paymentMethod;
        if (filterMethod === 'cash') return m === 'cash';
        if (filterMethod === 'lipa_number') return m === 'lipa_number' || m === 'mpesa';
        if (filterMethod === 'legacy') return m === 'card' || m === 'other';
        return true;
      });
    }
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      filtered = filtered.filter(
        (s) =>
          (s.product?.name || '').toLowerCase().includes(q) ||
          (s.paymentLabel || '').toLowerCase().includes(q) ||
          (s.paymentMethod || '').toLowerCase().includes(q)
      );
    }
    filtered = [...filtered].sort((a, b) => {
      if (sortBy === 'date' || sortBy === 'latest') {
        return sortDir === 'desc' || sortBy === 'latest'
          ? new Date(b.date) - new Date(a.date)
          : new Date(a.date) - new Date(b.date);
      }
      if (sortBy === 'amount') {
        return sortDir === 'desc' ? (b.amount || 0) - (a.amount || 0) : (a.amount || 0) - (b.amount || 0);
      }
      if (sortBy === 'profit' && isOwner) {
        return sortDir === 'desc' ? (b.profit || 0) - (a.profit || 0) : (a.profit || 0) - (b.profit || 0);
      }
      if (sortBy === 'category') {
        const catA = (a.product?.category || '').toLowerCase();
        const catB = (b.product?.category || '').toLowerCase();
        if (catA < catB) return sortDir === 'desc' ? 1 : -1;
        if (catA > catB) return sortDir === 'desc' ? -1 : 1;
        return 0;
      }
      return 0;
    });
    return filtered;
  }, [list, search, filterMethod, sortBy, sortDir, isOwner]);

  const totals = useMemo(() => {
    return filteredSales.reduce(
      (acc, s) => {
        const qty = Number(s.quantity) || 0;
        const revenue = parseFloat(s.amount) || 0;
        const cost = parseFloat(s.cost);
        const costN = Number.isNaN(cost) ? 0 : cost;
        let profit = parseFloat(s.profit);
        if (Number.isNaN(profit)) {
          const a = parseFloat(s.amount);
          const c = parseFloat(s.cost);
          profit = !Number.isNaN(a) && !Number.isNaN(c) ? a - c : 0;
        }
        return {
          qty: acc.qty + qty,
          revenue: acc.revenue + revenue,
          cost: acc.cost + costN,
          profit: acc.profit + profit,
        };
      },
      { qty: 0, revenue: 0, cost: 0, profit: 0 }
    );
  }, [filteredSales]);

  const handleRefresh = useCallback(() => {
    if (typeof onRefresh === 'function') onRefresh();
  }, [onRefresh]);

  const handleDownloadSales = useCallback(() => {
    if (!isOwner) {
      setExportError('Only owner can export reports.');
      return;
    }
    if (filteredSales.length === 0) return;
    setExportError('');
    const tag = filterMethod === 'all' ? 'all' : filterMethod;
    const head = isOwner ? SALES_CSV_HEAD_OWNER : SALES_CSV_HEAD_STAFF;
    const rows = buildSalesExportRows(filteredSales, isOwner);
    downloadCsv(`sales-history-${tag}-${new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19)}.csv`, [
      head,
      ...rows,
    ]);
  }, [filteredSales, filterMethod, isOwner]);

  const handleDownloadSalesPdf = useCallback(async () => {
    if (!isOwner) {
      setExportError('Only owner can export reports.');
      return;
    }
    if (filteredSales.length === 0) return;
    setPdfExporting(true);
    setExportError('');
    try {
      const data = buildSalesExportRows(filteredSales, isOwner);
      const head = isOwner ? SALES_PDF_HEAD_OWNER : SALES_PDF_HEAD_STAFF;
      const filterTitles = { all: 'All methods', cash: 'Cash', lipa_number: 'Lipa number', legacy: 'Other (legacy)' };
      const methodLabel = filterTitles[filterMethod] || filterMethod;
      await exportTableToPdf(`Sales history – ${methodLabel} (${data.length} sales)`, head, data);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(e);
      setExportError('Could not create PDF. Check your connection and try again.');
    } finally {
      setPdfExporting(false);
    }
  }, [filteredSales, filterMethod, isOwner]);

  const sortDirToggle = sortBy === 'latest' ? 'desc' : sortDir;

  return (
    <div className="sales-history-page page-bleed">
      <header className="sales-history-header">
        <div className="sales-history-header-text">
          <h1 className="sales-history-title">Sales history</h1>
          <p className="sales-history-lede">
            {isOwner
              ? 'Filter and sort recorded POS sales. Download as CSV (Excel) or PDF. Totals reflect the current filter.'
              : 'Filter and sort your recorded POS sales. Download as CSV (Excel) or PDF.'}
          </p>
        </div>
        <div className="sales-history-header-actions">
          {isOwner ? (
            <>
              <button
                type="button"
                className="sales-history-download"
                onClick={handleDownloadSales}
                disabled={loading || filteredSales.length === 0 || pdfExporting}
                title="Spreadsheet (CSV) — current filters"
              >
                Download CSV
              </button>
              <button
                type="button"
                className="sales-history-download-pdf"
                onClick={() => void handleDownloadSalesPdf()}
                disabled={loading || filteredSales.length === 0 || pdfExporting}
                title="PDF report — same data as the table"
              >
                {pdfExporting ? 'PDF…' : 'Download PDF'}
              </button>
            </>
          ) : null}
          <button
            type="button"
            className="sales-history-refresh"
            onClick={handleRefresh}
            disabled={loading || pdfExporting}
          >
            {loading ? 'Loading…' : 'Refresh'}
          </button>
        </div>
      </header>

      {exportError && (
        <p className="sales-history-export-error" role="alert">
          {exportError}
        </p>
      )}

      <div className="sales-history-toolbar">
        <div className="sales-history-field sales-history-field--grow">
          <label className="sales-history-label" htmlFor="sales-history-search">
            Search
          </label>
          <input
            id="sales-history-search"
            className="sales-history-input"
            type="search"
            placeholder="Product name or payment…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            autoComplete="off"
          />
        </div>
        <div className="sales-history-field">
          <label className="sales-history-label" htmlFor="sales-history-pay">
            Payment
          </label>
          <select
            id="sales-history-pay"
            className="sales-history-select"
            value={filterMethod}
            onChange={(e) => setFilterMethod(e.target.value)}
          >
            {paymentMethods.map((m) => (
              <option key={m.value} value={m.value}>
                {m.label}
              </option>
            ))}
          </select>
        </div>
        <div className="sales-history-field">
          <label className="sales-history-label" htmlFor="sales-history-sort">
            Sort by
          </label>
          <select
            id="sales-history-sort"
            className="sales-history-select"
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
          >
            <option value="date">Date</option>
            <option value="latest">Latest first</option>
            <option value="amount">Revenue</option>
            {isOwner ? <option value="profit">Profit</option> : null}
            <option value="category">Category</option>
          </select>
        </div>
        <div className="sales-history-field sales-history-field--dir">
          <span className="sales-history-label">Order</span>
          {sortBy === 'latest' ? (
            <span className="sales-history-dir-static" title="This sort is always newest first">
              Newest first
            </span>
          ) : (
            <button
              type="button"
              className="sales-history-dir-btn"
              title={sortDirToggle === 'desc' ? 'Newest / highest first' : 'Oldest / lowest first'}
              onClick={() => setSortDir((d) => (d === 'desc' ? 'asc' : 'desc'))}
            >
              {sortDirToggle === 'desc' ? '↓ High → low' : '↑ Low → high'}
            </button>
          )}
        </div>
      </div>

      <p className="sales-history-meta" aria-live="polite">
        {loading ? 'Loading sales…' : `${filteredSales.length} sale${filteredSales.length !== 1 ? 's' : ''} shown`}
        {!loading && list.length > 0 && filteredSales.length !== list.length && (
          <span className="sales-history-meta-sub"> ({list.length} total)</span>
        )}
      </p>

      <div className="sales-history-card">
        {loading && list.length === 0 ? (
          <div className="sales-history-empty sales-history-empty--loading">
            <p>Loading sales…</p>
          </div>
        ) : filteredSales.length === 0 ? (
          <div className="sales-history-empty">
            <p className="sales-history-empty-title">No sales match</p>
            <p className="sales-history-empty-text">
              {list.length === 0
                ? 'Record a sale from POS to see it here.'
                : 'Try changing search or payment filter.'}
            </p>
          </div>
        ) : (
          <div className="sales-history-scroll">
            <table className="sales-history-table">
              <thead>
                <tr>
                  <th className="sales-history-th sales-history-th--num">#</th>
                  <th className="sales-history-th">Product</th>
                  <th className="sales-history-th">Category</th>
                  <th className="sales-history-th sales-history-th--num">Qty</th>
                  <th className="sales-history-th">Payment</th>
                  <th className="sales-history-th sales-history-th--num">Revenue (TZS)</th>
                  {isOwner ? <th className="sales-history-th sales-history-th--num">Cost (TZS)</th> : null}
                  {isOwner ? <th className="sales-history-th sales-history-th--num">Profit (TZS)</th> : null}
                  <th className="sales-history-th">Date</th>
                </tr>
              </thead>
              <tbody>
                {filteredSales.map((sale, idx) => (
                  <tr key={sale.id != null ? String(sale.id) : idx}>
                    <td className="sales-history-td sales-history-td--num sales-history-td--muted">{idx + 1}</td>
                    <td className="sales-history-td sales-history-td--strong">{sale.product?.name || '—'}</td>
                    <td className="sales-history-td sales-history-td--muted">{sale.product?.category || '—'}</td>
                    <td className="sales-history-td sales-history-td--num">{sale.quantity}</td>
                    <td className="sales-history-td sales-history-td--pay">
                      {sale.paymentLabel ||
                        (sale.paymentMethod === 'cash'
                          ? 'Cash'
                          : sale.paymentMethod === 'lipa_number'
                            ? 'Lipa number'
                            : sale.paymentMethod || '—')}
                    </td>
                    <td className="sales-history-td sales-history-td--num">
                      {sale.amount != null ? Number(sale.amount).toLocaleString() : '—'}
                    </td>
                    {isOwner ? (
                      <td className="sales-history-td sales-history-td--num sales-history-td--muted">
                        {sale.cost != null ? Number(sale.cost).toLocaleString() : '—'}
                      </td>
                    ) : null}
                    {isOwner ? (
                      <td className="sales-history-td sales-history-td--num sales-history-td--profit">
                        {sale.profit != null ? Number(sale.profit).toLocaleString() : '—'}
                      </td>
                    ) : null}
                    <td className="sales-history-td sales-history-td--date">{formatSaleDate(sale.date)}</td>
                  </tr>
                ))}
              </tbody>
              {isOwner ? (
                <tfoot>
                  <tr className="sales-history-tfoot">
                    <td className="sales-history-td sales-history-td--foot" colSpan={3}>
                      <strong>Totals</strong>
                      <span className="sales-history-foot-hint"> (filtered)</span>
                    </td>
                    <td className="sales-history-td sales-history-td--num sales-history-td--foot">
                      <strong>{totals.qty.toLocaleString()}</strong>
                    </td>
                    <td className="sales-history-td sales-history-td--foot" />
                    <td className="sales-history-td sales-history-td--num sales-history-td--foot">
                      <strong>{Math.round(totals.revenue).toLocaleString()}</strong>
                    </td>
                    <td className="sales-history-td sales-history-td--num sales-history-td--foot">
                      <strong>{Math.round(totals.cost).toLocaleString()}</strong>
                    </td>
                    <td className="sales-history-td sales-history-td--num sales-history-td--foot sales-history-td--profit-foot">
                      <strong>{Math.round(totals.profit).toLocaleString()}</strong>
                    </td>
                    <td className="sales-history-td sales-history-td--foot" />
                  </tr>
                </tfoot>
              ) : null}
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default SalesHistory;
